import React, {Component} from 'react';
import {loadModules} from 'esri-loader';

export default class MultiPolygonDraw extends Component {
    constructor(props) {
        super(props);

        this.state = {
            locked: true,
            mode: false,
        };

        this.sketchViewModel = null;
        this.editGraphic = null;
        this.polygons = [];
    }

    componentDidMount() {
        loadModules([
            'esri/widgets/Sketch/SketchViewModel',
        ]).then(([SketchViewModel]) => {

            this.props.view.ui.add(["polygon-button", "lock-button", "trash-button"], 'bottom-left');

            // Create a symbol for rendering the graphic
            const polygonSymbol = {
                type: "simple-fill",
                color: [247, 132, 12, 0.8],
                style: "solid",
                outline: {
                    color: 'red',
                    width: 1
                }
            };

            this.props.view.when(() => {
                // create a new sketch view model
                const sketchViewModel = new SketchViewModel({
                    view: this.props.view,
                    layer: this.props.map.findLayerById(this.props.layerId),
                    updateOnGraphicClick: false,
                    polygonSymbol,
                });

                this.setUpClickHandler();

                this.sketchViewModel = sketchViewModel;
                // Listen to create-complete event to add a newly created graphic to view
                sketchViewModel.on(["create"], this.addGraphic);
                // Listen the sketchViewModel's update-complete and update-cancel events
                sketchViewModel.on(["update"], this.updateGraphic);

                if (this.props.polygons) {
                    for (let polygon of this.props.polygons.multipolygon) {
                        this.addGraphicFromProps(polygon);
                    }
                }
            });

        }).catch(console.error);
    }

    componentWillUnmount() {
        this.props.view.ui.remove(["polygon-button", "lock-button", "trash-button"]);
        this.props.view.graphics.removeAll();
        this.props.view.ui.remove(this.sketchViewModel);
    }

    handleRemove = () => {
        this.setActiveButton('delete');
    };

    handleCreate = () => {
        this.setActiveButton('draw');
    };

    handleLock = () => {
        this.setActiveButton('edit');
    };

    setActiveButton = (mode) => {
        return new Promise((resolve) => {
            if (this.state.mode === 'draw') {
                this.sketchViewModel.cancel();
            } else if (this.state.mode === 'edit') {
                this.sketchViewModel.complete();
            }

            this.setState(prevState => ({
                mode: (prevState.mode === mode) ? false : mode,
            }), () => {
                if (this.state.mode === 'draw') {
                    this.sketchViewModel.create("polygon");
                }
                resolve();
            });
        });
    };

    // add graphic from properties
    addGraphicFromProps = (geometry) => {
        loadModules(['esri/Graphic']).then(([Graphic]) => {
            let polygon = {
                type: geometry.type,
                rings: geometry.coordinates[0],
                spatialReference: {
                    wkid: 3301
                }
            };

            const graphic = new Graphic({
                geometry: polygon,
                symbol: this.sketchViewModel.polygonSymbol,
            });

            this.polygons = [...this.polygons, {
                id: graphic.uid,
                type: geometry.type,
                coordinates: geometry.coordinates
            }];

            this.props.map.findLayerById(this.props.layerId).add(graphic);
            this.handleUpdate();

        }).catch(console.error);
    };

    // called when sketchViewModel's create-complete event is fired.
    addGraphic = (event) => {
        loadModules(['esri/Graphic']).then(([Graphic]) => {

            const graphic = new Graphic({
                geometry: event.geometry,
                symbol: this.sketchViewModel.polygonSymbol,
            });

            this.props.map.findLayerById(this.props.layerId).add(graphic);

            if (event.state === 'complete') {
                let newPolygon = {
                    id: event.graphic.uid,
                    type: event.graphic.geometry.type,
                    coordinates: event.graphic.geometry.rings,
                };

                this.polygons = [...this.polygons, newPolygon];
                this.handleUpdate();
                this.setState({mode: false});
            }
        }).catch(console.error);
    };

    updateGraphic = (event) => {
        loadModules(['esri/Graphic']).then(([Graphic]) => {
            const graphic = new Graphic({
                geometry: event.geometry,
                symbol: this.sketchViewModel.polygonSymbol,
            });

            this.props.map.findLayerById(this.props.layerId).add(graphic);
            this.editGraphic = null;
            if (event.state === 'complete') {
                let newPolygon = {
                    id: event.graphics[0].uid,
                    type: event.graphics[0].geometry.type,
                    coordinates: event.graphics[0].geometry.rings,
                };

                let polygons = this.polygons.filter(polygon => polygon.id !== newPolygon.id);
                this.polygons = [...polygons, newPolygon];
                this.handleUpdate();
            }
        }).catch(console.error);
    };

    // set up logic to handle geometry update and reflect the update on "graphicsLayer"
    setUpClickHandler = () => {
        this.props.view.on("click", (event) => {
            if (!['edit', 'delete'].includes(this.state.mode)) return;
            this.props.view.hitTest(event).then((response) => {
                let results = response.results;
                if (results.length > 0 && results[0].graphic.layer.id === this.props.layerId) {
                    // Check if we're already editing a graphic
                    if (this.state.mode === 'edit' && !this.editGraphic) {
                        this.editGraphic = results[0].graphic;
                        this.sketchViewModel.update(this.editGraphic);
                    } else if (this.state.mode === 'delete') {
                        let index = this.polygons.findIndex(polygon => polygon.id === results[0].graphic.uid);
                        if (index > -1) {
                            this.polygons.splice(index, 1);
                            this.props.map.findLayerById(this.props.layerId).graphics.remove(results[0].graphic);
                            this.handleUpdate();
                            this.setState({mode: false});
                        }
                    }
                }
            });
        });
    };

    handleUpdate = () => {
        if (this.props.update) {
            this.props.update(this.polygons);
        }
    };

    showPolygon = (polygonId) => {
        let layer = this.props.map.findLayerById(this.props.layerId),
            graphic = layer.graphics.find(graphic => graphic.uid === polygonId);
        if (graphic) {
            this.props.view.goTo(graphic);
        }
    };

    editPolygon = (polygonId) => {
        this.setActiveButton(false).then(() => {
            this.showPolygon(polygonId);
            this.setState({
                mode: 'edit',
            }, () => {
                let index = this.polygons.findIndex(polygon => polygon.id === polygonId);
                if (index > -1) {
                    let layer = this.props.map.findLayerById(this.props.layerId),
                        graphic = layer.graphics.find(graphic => graphic.uid === polygonId);
                    if (graphic) {
                        this.editGraphic = graphic;
                        this.sketchViewModel.update(this.editGraphic);
                    }
                }
            });
        });
    };

    removePolygon = (polygonId) => {
        this.setActiveButton(false).then(() => {
            let index = this.polygons.findIndex(polygon => polygon.id === polygonId);
            if (index > -1) {
                let layer = this.props.map.findLayerById(this.props.layerId),
                    graphic = layer.graphics.find(graphic => graphic.uid === polygonId);
                if (graphic) {
                    this.polygons.splice(index, 1);
                    layer.graphics.remove(graphic);
                    this.handleUpdate();
                }
            }
        });
    };

    render() {
        return (
            <div>
                <div
                    style={{backgroundColor: this.state.mode === 'draw' && '#f1c71a'}}
                    id="polygon-button"
                    onClick={this.handleCreate}
                    className="esri-widget esri-widget--button esri-interactive"
                >
                    <span className="esri-icon-polygon"/>
                </div>
                <div
                    style={{backgroundColor: this.state.mode === 'edit' && '#f1c71a'}}
                    id="lock-button"
                    onClick={this.handleLock}
                    className="esri-widget esri-widget--button esri-interactive"
                >
                    {this.state.mode !== 'edit'
                        ? <span className="esri-icon-locked"/>
                        : <span className="esri-icon-unlocked"/>
                    }
                </div>
                <div
                    style={{backgroundColor: this.state.mode === 'delete' && '#f1c71a'}}
                    id="trash-button"
                    onClick={this.handleRemove}
                    className="esri-widget esri-widget--button esri-interactive"
                >
                    <span className="esri-icon-trash"/>
                </div>
            </div>
        )
    }


}

MultiPolygonDraw.defaultProps = {
  geometryServerURL: 'http://sampleserver6.arcgisonline.com/arcgis/rest/services/Utilities/Geometry/GeometryServer'
};
